import { useState, useEffect, useCallback } from "react";
import {
  X,
  RotateCw,
  Calendar,
  Clock,
  CheckCircle2,
  AlertTriangle,
  FileText,
  Users,
  Eye,
  Play,
  Pause,
  StopCircle,
  Edit3,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { isAdmin } from "../../lib/permissions";

function formatFriendlyDate(dateStr, timeStr) {
  if (!dateStr) return "—";
  let formatted = dateStr;
  try {
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) {
      formatted = d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    }
  } catch {}

  if (timeStr && timeStr.trim()) {
    return `${formatted}, ${timeStr.trim()}`;
  }
  return formatted;
}

export default function RecurringTaskHistoryModal({
  recurringTaskId,
  user,
  onClose,
  onOpenTask,
  onEditRecurring,
}) {
  const isSuperOrAdmin = isAdmin(user);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionBusy, setActionBusy] = useState(false);

  const loadDetail = useCallback(async () => {
    if (!recurringTaskId) return;
    setLoading(true);
    try {
      const res = await api("get", `/tasks/recurring/${recurringTaskId}`);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load recurring task details");
      onClose();
    } finally {
      setLoading(false);
    }
  }, [recurringTaskId, onClose]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const handlePause = async () => {
    setActionBusy(true);
    try {
      await api("patch", `/tasks/recurring/${recurringTaskId}/pause`);
      toast.success("Recurring task paused");
      loadDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to pause");
    } finally {
      setActionBusy(false);
    }
  };

  const handleResume = async () => {
    setActionBusy(true);
    try {
      await api("patch", `/tasks/recurring/${recurringTaskId}/resume`);
      toast.success("Recurring task resumed");
      loadDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resume");
    } finally {
      setActionBusy(false);
    }
  };

  const handleStop = async () => {
    if (!window.confirm("Are you sure you want to stop this recurring task? No future occurrences will be created.")) return;
    setActionBusy(true);
    try {
      await api("patch", `/tasks/recurring/${recurringTaskId}/stop`);
      toast.success("Recurring task ended");
      loadDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to stop task");
    } finally {
      setActionBusy(false);
    }
  };

  const getStatusBadge = (st) => {
    const s = (st || "active").toLowerCase();
    if (s === "active") return { bg: "#ecfdf5", text: "#065f46", border: "#a7f3d0", label: "Active" };
    if (s === "paused") return { bg: "#fffbeb", text: "#92400e", border: "#fde68a", label: "Paused" };
    return { bg: "#f1f5f9", text: "#475569", border: "#cbd5e1", label: "Stopped" };
  };

  const getOccStatusClass = (s) => {
    const st = (s || "Pending").toLowerCase();
    if (st === "completed") return "badge green";
    if (st === "in progress") return "badge blue";
    if (st === "overdue") return "badge red";
    return "badge amber";
  };

  return (
    <div className="modal-backdrop" data-testid="recurring-history-backdrop">
      <div
        className="modal"
        style={{ maxWidth: 740, width: "100%", maxHeight: "90vh", overflowY: "auto" }}
        data-testid="recurring-history-modal"
      >
        <div className="modal-head" style={{ marginBottom: 16 }}>
          <div>
            <div className="small-label" style={{ letterSpacing: "1.2px", color: "#64748b" }}>
              RECURRING TASK DEFINITION & HISTORY
            </div>
            <h2 style={{ fontSize: 20, marginTop: 4, display: "flex", alignItems: "center", gap: 8 }}>
              <RotateCw size={18} color="#2563eb" />
              <span>{data?.title || (loading ? "Loading..." : "Recurring Task")}</span>
            </h2>
          </div>
          <button type="button" className="icon-btn" onClick={onClose} data-testid="close-history-modal">
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div style={{ padding: 48, textAlign: "center", color: "#64748b" }}>
            Loading recurring schedule and history...
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Top Definition Summary Banner */}
            <div
              style={{
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 8,
                padding: "14px 16px",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10, marginBottom: 10 }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#1d4ed8",
                        background: "#eff6ff",
                        padding: "2px 8px",
                        borderRadius: 12,
                        border: "1px solid #bfdbfe",
                      }}
                    >
                      {data?.recurrence_summary || "Recurring"}
                    </span>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        padding: "2px 8px",
                        borderRadius: 12,
                        background: getStatusBadge(data?.status).bg,
                        color: getStatusBadge(data?.status).text,
                        border: `1px solid ${getStatusBadge(data?.status).border}`,
                      }}
                    >
                      {getStatusBadge(data?.status).label}
                    </span>
                    <span className="badge slate" style={{ fontSize: 11 }}>
                      {data?.company_name || "Pinaki Solutions"}
                    </span>
                    <span className="badge blue" style={{ fontSize: 11 }}>
                      {data?.priority || "Normal"}
                    </span>
                  </div>
                  {data?.description && (
                    <p style={{ fontSize: 12.5, color: "#475569", margin: "4px 0 0" }}>
                      {data.description}
                    </p>
                  )}
                </div>

                {/* Action Buttons for Admins */}
                {isSuperOrAdmin && (
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    {data?.status === "active" ? (
                      <button
                        className="outline-btn compact"
                        onClick={handlePause}
                        disabled={actionBusy}
                        title="Pause occurrences"
                        style={{ fontSize: 11.5 }}
                      >
                        <Pause size={12} /> Pause
                      </button>
                    ) : data?.status === "paused" ? (
                      <button
                        className="outline-btn compact"
                        onClick={handleResume}
                        disabled={actionBusy}
                        title="Resume occurrences"
                        style={{ fontSize: 11.5 }}
                      >
                        <Play size={12} /> Resume
                      </button>
                    ) : null}

                    {data?.status !== "stopped" && (
                      <button
                        className="outline-btn danger compact"
                        onClick={handleStop}
                        disabled={actionBusy}
                        title="End recurring task"
                        style={{ fontSize: 11.5 }}
                      >
                        <StopCircle size={12} /> Stop
                      </button>
                    )}

                    {onEditRecurring && (
                      <button
                        className="outline-btn compact"
                        onClick={() => {
                          onClose();
                          onEditRecurring(data);
                        }}
                        style={{ fontSize: 11.5 }}
                      >
                        <Edit3 size={12} /> Edit
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Assignees and Schedule Info Row */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.5fr 1fr 1fr",
                  gap: 12,
                  paddingTop: 10,
                  borderTop: "1px solid #e2e8f0",
                  fontSize: 12,
                }}
              >
                <div>
                  <span style={{ color: "#64748b", display: "block", marginBottom: 3, fontWeight: 500 }}>
                    Assigned Team Members:
                  </span>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {(data?.assignees || []).map((a) => (
                      <span
                        key={a.user_id}
                        style={{
                          background: "#ffffff",
                          border: "1px solid #cbd5e1",
                          borderRadius: 4,
                          padding: "1px 6px",
                          fontSize: 11,
                          fontWeight: 500,
                          color: "#1e293b",
                        }}
                      >
                        {a.name} <small style={{ color: "#64748b" }}>({a.department || ""})</small>
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <span style={{ color: "#64748b", display: "block", marginBottom: 2, fontWeight: 500 }}>
                    Start / End Date:
                  </span>
                  <span style={{ color: "#1e293b", fontWeight: 600 }}>
                    {formatFriendlyDate(data?.start_date)}
                    {data?.end_date ? ` to ${formatFriendlyDate(data?.end_date)}` : " (Ongoing)"}
                  </span>
                </div>

                <div>
                  <span style={{ color: "#64748b", display: "block", marginBottom: 2, fontWeight: 500 }}>
                    Next Scheduled Run:
                  </span>
                  <span style={{ color: data?.status === "active" ? "#16a34a" : "#64748b", fontWeight: 600 }}>
                    {data?.status === "active" && data?.next_run_date
                      ? `${formatFriendlyDate(data.next_run_date, data.time_of_day)}`
                      : data?.status === "paused"
                      ? "Paused"
                      : "Ended"}
                  </span>
                </div>
              </div>
            </div>

            {/* OCCURRENCE HISTORY SECTION */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0, color: "var(--navy)" }}>
                  Occurrence History ({data?.occurrences?.length || 0})
                </h3>
                <span style={{ fontSize: 11.5, color: "#64748b" }}>
                  {data?.completed_occurrences || 0} completed · {data?.pending_occurrences || 0} pending
                </span>
              </div>

              {(!data?.occurrences || data.occurrences.length === 0) ? (
                <div
                  style={{
                    background: "#f8fafc",
                    border: "1px dashed #cbd5e1",
                    borderRadius: 8,
                    padding: 24,
                    textAlign: "center",
                    color: "#64748b",
                    fontSize: 13,
                  }}
                >
                  No occurrences generated yet. The first occurrence will be generated on {formatFriendlyDate(data?.next_run_date || data?.start_date)}.
                </div>
              ) : (
                <div style={{ border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
                  <table className="panel-table" style={{ margin: 0 }}>
                    <thead>
                      <tr>
                        <th style={{ width: "22%" }}>Scheduled Date</th>
                        <th style={{ width: "15%" }}>Status</th>
                        <th style={{ width: "38%" }}>Latest Progress Report</th>
                        <th style={{ width: "15%", textAlign: "right" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.occurrences.map((occ) => {
                        const lastRep = occ.last_report;
                        return (
                          <tr key={occ.task_id} data-testid={`history-occ-${occ.task_id}`}>
                            <td>
                              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                <Calendar size={12} color="#64748b" />
                                <b style={{ fontSize: 12.5, color: "#0f172a" }}>
                                  {formatFriendlyDate(occ.due_date, occ.due_time)}
                                </b>
                              </div>
                              <small className="mono" style={{ color: "#94a3b8" }}>
                                {occ.task_id}
                              </small>
                            </td>
                            <td>
                              <span className={getOccStatusClass(occ.status)}>{occ.status}</span>
                            </td>
                            <td>
                              {lastRep ? (
                                <div>
                                  <span style={{ fontSize: 12, color: "#1e293b", display: "block" }}>
                                    {lastRep.report.length > 70
                                      ? lastRep.report.slice(0, 70) + "..."
                                      : lastRep.report}
                                  </span>
                                  <small style={{ color: "#64748b" }}>
                                    by {lastRep.user_name || "User"}
                                  </small>
                                </div>
                              ) : (
                                <span style={{ color: "#94a3b8", fontSize: 11.5, fontStyle: "italic" }}>
                                  No reports submitted
                                </span>
                              )}
                            </td>
                            <td style={{ textAlign: "right" }}>
                              <button
                                type="button"
                                className="primary-btn compact"
                                style={{ height: 26, fontSize: 11, padding: "0 8px" }}
                                onClick={() => {
                                  onClose();
                                  if (onOpenTask) onOpenTask(occ.task_id);
                                }}
                                data-testid={`open-occ-${occ.task_id}`}
                              >
                                Open Task <ExternalLink size={10} style={{ marginLeft: 3 }} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* UPCOMING SCHEDULE PREVIEW */}
            {data?.upcoming_occurrences && data.upcoming_occurrences.length > 0 && (
              <div>
                <h4 style={{ fontSize: 12.5, fontWeight: 700, margin: "0 0 6px", color: "var(--navy)" }}>
                  Upcoming Schedule Preview
                </h4>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {data.upcoming_occurrences.map((u, i) => (
                    <div
                      key={i}
                      style={{
                        background: "#f1f5f9",
                        border: "1px solid #cbd5e1",
                        borderRadius: 6,
                        padding: "5px 10px",
                        fontSize: 11.5,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <Clock size={11} color="#64748b" />
                      <span style={{ fontWeight: 600, color: "#1e293b" }}>{u.formatted_date}</span>
                      <span style={{ color: "#64748b" }}>at {u.time}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20, paddingTop: 12, borderTop: "1px solid var(--border)" }}>
          <button type="button" className="outline-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
